<template>
  <div class="workshops-page">
    <!-- Hero Section -->
    <section class="page-hero">
      <div class="hero-overlay"></div>
      <div class="container">
        <div class="hero-content" data-aos="fade-up">
          <h1 class="page-title">Creative Workshops</h1>
          <p class="page-subtitle">Learn the art of handcrafting with our expert-led workshops. Create something beautiful with your own hands!</p>
        </div>
      </div>
    </section>

    <!-- Filter & Search Section -->
    <section class="filter-section">
      <div class="container">
        <div class="filter-wrapper" data-aos="fade-up">
          <div class="filter-group">
            <label class="filter-label">Category:</label>
            <select v-model="selectedCategory" class="filter-select">
              <option value="all">All Workshops</option>
              <option value="jewelry">Jewelry Making</option>
              <option value="crafts">Crafts & Decor</option>
              <option value="painting">Painting & Art</option>
              <option value="pottery">Pottery & Clay</option>
            </select>
          </div>
          
          <div class="filter-group">
            <label class="filter-label">Price Range:</label>
            <select v-model="selectedPrice" class="filter-select">
              <option value="all">All Prices</option>
              <option value="0-500">Under ₹500</option>
              <option value="500-1000">₹500 - ₹1000</option>
              <option value="1000-2000">₹1000 - ₹2000</option>
              <option value="2000+">₹2000+</option>
            </select>
          </div>
          
          <div class="filter-group">
            <label class="filter-label">Date:</label>
            <select v-model="selectedDate" class="filter-select">
              <option value="all">All Dates</option>
              <option value="upcoming">Upcoming</option>
              <option value="this-week">This Week</option>
              <option value="this-month">This Month</option>
            </select>
          </div>
        </div>
      </div>
    </section>

    <!-- Workshops Grid -->
    <section class="workshops-grid">
      <div class="container">
        <!-- No workshops message -->
        <div v-if="filteredWorkshops.length === 0" class="no-workshops" data-aos="fade-up">
          <i class="fas fa-calendar-times"></i>
          <h3>No workshops found</h3>
          <p>Try adjusting your filters or check back soon for new workshops!</p>
        </div>
        
        <!-- Workshops list -->
        <div v-else class="row g-4">
          <div
            class="col-lg-6"
            v-for="(workshop, index) in filteredWorkshops"
            :key="workshop._id"
            :data-aos="'fade-up'"
            :data-aos-delay="index * 100"
          >
            <div class="workshop-card">
              <!-- Workshop Image Gallery -->
              <div class="workshop-image-section">
                <div class="main-image-wrapper">
                  <img 
                    :src="workshop.images && workshop.images.length > 0 ? workshop.images[0] : 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=600&h=400&fit=crop'" 
                    :alt="workshop.title" 
                    class="workshop-main-image"
                  >
                  
                  <!-- Availability Badge -->
                  <div class="availability-badge" :class="getAvailabilityClass(workshop)">
                    {{ getAvailabilityText(workshop) }}
                  </div>
                  
                  <!-- Category Tag -->
                  <div class="category-tag">
                    <i class="fas fa-tag"></i>
                    {{ workshop.category || 'General' }}
                  </div>
                </div>
                
                <!-- Thumbnail Gallery (if multiple images) -->
                <div v-if="workshop.images && workshop.images.length > 1" class="thumbnail-gallery">
                  <div 
                    v-for="(image, imgIndex) in workshop.images.slice(0, 4)" 
                    :key="imgIndex"
                    class="thumbnail-item"
                  >
                    <img :src="image" :alt="`${workshop.title} - ${imgIndex + 1}`">
                    <div v-if="imgIndex === 3 && workshop.images.length > 4" class="more-images">
                      +{{ workshop.images.length - 4 }}
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Workshop Details -->
              <div class="workshop-details">
                <h3 class="workshop-title">{{ workshop.title }}</h3>
                <p class="workshop-description">{{ workshop.description || 'Join us for an exciting hands-on workshop where you\'ll learn new skills and create something beautiful!' }}</p>
                
                <div class="workshop-info">
                  <div class="info-row">
                    <div class="info-item">
                      <i class="fas fa-calendar-alt"></i>
                      <span>{{ formatDate(workshop.date) }}</span>
                    </div>
                    <div class="info-item">
                      <i class="fas fa-clock"></i>
                      <span>{{ workshop.time }}</span>
                    </div>
                  </div>
                  
                  <div class="info-row">
                    <div class="info-item">
                      <i class="fas fa-map-marker-alt"></i>
                      <span>{{ workshop.address }}</span>
                    </div>
                  </div>
                  
                  <div class="info-row">
                    <div class="info-item">
                      <i class="fas fa-users"></i>
                      <span>{{ workshop.maxParticipants || 15 }} participants max</span>
                    </div>
                    <div class="info-item">
                      <i class="fas fa-user-check"></i>
                      <span>{{ workshop.registeredCount || 0 }} registered</span>
                    </div>
                  </div>
                  
                  <div class="info-row" v-if="workshop.duration">
                    <div class="info-item">
                      <i class="fas fa-hourglass-half"></i>
                      <span>{{ workshop.duration }}</span>
                    </div>
                    <div class="info-item" v-if="workshop.difficulty">
                      <i class="fas fa-signal"></i>
                      <span>{{ workshop.difficulty }}</span>
                    </div>
                  </div>
                </div>
                
                <!-- What You'll Learn -->
                <div v-if="workshop.learningPoints && workshop.learningPoints.length > 0" class="learning-section">
                  <h4 class="section-subtitle">What You'll Learn:</h4>
                  <ul class="learning-list">
                    <li v-for="(point, idx) in workshop.learningPoints.slice(0, 3)" :key="idx">
                      <i class="fas fa-check-circle"></i>
                      {{ point }}
                    </li>
                  </ul>
                </div>
                
                <!-- Materials Included -->
                <div v-if="workshop.materialsIncluded" class="materials-badge">
                  <i class="fas fa-box"></i>
                  <span>All materials included</span>
                </div>
                
                <!-- Footer with Price & CTA -->
                <div class="workshop-footer">
                  <div class="price-section">
                    <span v-if="workshop.originalPrice" class="price-original">₹{{ workshop.originalPrice }}</span>
                    <span class="price-current">₹{{ workshop.price }}</span>
                    <span v-if="workshop.originalPrice" class="discount-badge">
                      Save {{ calculateDiscount(workshop.originalPrice, workshop.price) }}%
                    </span>
                  </div>
                  <button 
                    @click="openRegistrationModal(workshop)" 
                    class="btn-register"
                    :disabled="!isAvailable(workshop)"
                  >
                    <i class="fas fa-user-plus"></i>
                    {{ getButtonText(workshop) }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Join Section -->
    <section class="why-join-section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title-modern">Why Join Our Workshops?</h2>
          <div class="title-underline"></div>
        </div>
        
        <div class="row g-4">
          <div class="col-md-3" data-aos="fade-up" data-aos-delay="0">
            <div class="benefit-card">
              <div class="benefit-icon">
                <i class="fas fa-chalkboard-teacher"></i>
              </div>
              <h4>Expert Instructors</h4>
              <p>Learn from experienced artisans and crafters</p>
            </div>
          </div>
          
          <div class="col-md-3" data-aos="fade-up" data-aos-delay="100">
            <div class="benefit-card">
              <div class="benefit-icon">
                <i class="fas fa-tools"></i>
              </div>
              <h4>All Materials Included</h4>
              <p>Everything you need is provided</p>
            </div>
          </div>
          
          <div class="col-md-3" data-aos="fade-up" data-aos-delay="200">
            <div class="benefit-card">
              <div class="benefit-icon">
                <i class="fas fa-certificate"></i>
              </div>
              <h4>Certificate</h4>
              <p>Receive a completion certificate</p>
            </div>
          </div>
          
          <div class="col-md-3" data-aos="fade-up" data-aos-delay="300">
            <div class="benefit-card">
              <div class="benefit-icon">
                <i class="fas fa-coffee"></i>
              </div>
              <h4>Refreshments</h4>
              <p>Enjoy complimentary snacks & drinks</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Registration Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="closeModal">
          <i class="fas fa-times"></i>
        </button>
        
        <div class="modal-header">
          <h3>Register for Workshop</h3>
          <p>{{ selectedWorkshop?.title }}</p>
        </div>
        
        <form @submit.prevent="submitRegistration" class="registration-form">
          <div class="form-group">
            <label class="form-label">Full Name *</label>
            <input 
              type="text" 
              v-model="formData.name" 
              class="form-control" 
              placeholder="Your name"
              required
            >
          </div>
          
          <div class="form-group">
            <label class="form-label">Email Address *</label>
            <input 
              type="email" 
              v-model="formData.email" 
              class="form-control" 
              placeholder="your@email.com"
              required
            >
          </div>
          
          <div class="form-group">
            <label class="form-label">Phone Number *</label>
            <input 
              type="tel" 
              v-model="formData.phone" 
              class="form-control" 
              placeholder="+91 98765 43210"
              required
            >
          </div>
          
          <div class="form-group">
            <label class="form-label">Number of Participants *</label>
            <select v-model="formData.participants" class="form-control" required>
              <option value="1">1 Person</option>
              <option value="2">2 People</option>
              <option value="3">3 People</option>
              <option value="4">4 People</option>
            </select>
          </div>
          
          <div class="form-group">
            <label class="form-label">Special Requirements (Optional)</label>
            <textarea 
              v-model="formData.requirements" 
              class="form-control" 
              rows="3"
              placeholder="Any dietary restrictions, accessibility needs, etc."
            ></textarea>
          </div>
          
          <div class="modal-footer">
            <div class="total-price">
              <span>Total:</span>
              <strong>₹{{ calculateTotal() }}</strong>
            </div>
            <button type="submit" class="btn-submit" :disabled="isSubmitting">
              <span v-if="!isSubmitting">
                <i class="fas fa-check-circle"></i>
                Confirm Registration
              </span>
              <span v-else>
                <i class="fas fa-spinner fa-spin"></i>
                Processing...
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from 'vue-toast-notification';
// import WorkshopService from '../services/WorkshopService'; // Uncomment when service is ready

export default {
  name: 'WorkshopsPage',
  data() {
    return {
      workshops: [],
      selectedCategory: 'all',
      selectedPrice: 'all',
      selectedDate: 'all',
      showModal: false,
      selectedWorkshop: null,
      isSubmitting: false,
      formData: {
        name: '',
        email: '',
        phone: '',
        participants: '1',
        requirements: ''
      }
    }
  },
  computed: {
    filteredWorkshops() {
      let filtered = this.workshops;
      
      // Filter by category
      if (this.selectedCategory !== 'all') {
        filtered = filtered.filter(w => w.category === this.selectedCategory);
      }
      
      // Filter by price
      if (this.selectedPrice !== 'all') {
        const [min, max] = this.selectedPrice.split('-').map(p => p.replace('+', ''));
        filtered = filtered.filter(w => {
          if (max) {
            return w.price >= parseInt(min) && w.price <= parseInt(max);
          } else {
            return w.price >= parseInt(min);
          }
        });
      }
      
      // Filter by date (implement based on your date logic)
      if (this.selectedDate !== 'all') {
        // Add your date filtering logic here
      }
      
      return filtered;
    }
  },
  methods: {
    async getWorkshops() {
      try {
        // Uncomment when backend is ready:
        // const response = await WorkshopService.getWorkshops();
        // this.workshops = response.data.data;
        
        // Mock data for demonstration
        this.workshops = [
          {
            _id: '1',
            title: 'Polymer Clay Jewelry Making',
            description: 'Create beautiful custom jewelry pieces using polymer clay. Perfect for beginners!',
            date: '2026-02-15',
            time: '10:00 AM - 1:00 PM',
            address: 'Bee\'s Atelier Studio, Ahmedabad',
            price: 1500,
            originalPrice: 2000,
            category: 'jewelry',
            maxParticipants: 15,
            registeredCount: 8,
            duration: '3 hours',
            difficulty: 'Beginner',
            materialsIncluded: true,
            learningPoints: [
              'Basic polymer clay techniques',
              'Color mixing and blending',
              'Creating earrings and pendants',
              'Baking and finishing'
            ],
            images: [
              'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=600&h=400&fit=crop',
              'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=600&h=400&fit=crop',
              'https://images.unsplash.com/photo-1611652022419-a9419f74343d?w=600&h=400&fit=crop'
            ]
          },
          {
            _id: '2',
            title: 'Handmade Bag Charm Workshop',
            description: 'Design and craft unique bag charms with various materials and techniques.',
            date: '2026-02-20',
            time: '2:00 PM - 5:00 PM',
            address: 'Bee\'s Atelier Studio, Ahmedabad',
            price: 1200,
            category: 'crafts',
            maxParticipants: 12,
            registeredCount: 12,
            duration: '3 hours',
            difficulty: 'Beginner',
            materialsIncluded: true,
            learningPoints: [
              'Charm design basics',
              'Working with resin',
              'Assembly techniques'
            ],
            images: [
              'https://images.unsplash.com/photo-1610701596007-11502861dcfa?w=600&h=400&fit=crop'
            ]
          }
        ];
      } catch (error) {
        const $toast = useToast();
        $toast.error('Failed to load workshops');
      }
    },
    
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
    },
    
    getAvailabilityClass(workshop) {
      const registered = workshop.registeredCount || 0;
      const max = workshop.maxParticipants || 15;
      
      if (registered >= max) return 'full';
      if (registered >= max * 0.8) return 'filling-fast';
      return 'available';
    },
    
    getAvailabilityText(workshop) {
      const registered = workshop.registeredCount || 0;
      const max = workshop.maxParticipants || 15;
      
      if (registered >= max) return 'Fully Booked';
      if (registered >= max * 0.8) return 'Filling Fast!';
      return `${max - registered} spots left`;
    },
    
    isAvailable(workshop) {
      const registered = workshop.registeredCount || 0;
      const max = workshop.maxParticipants || 15;
      return registered < max;
    },
    
    getButtonText(workshop) {
      if (!this.isAvailable(workshop)) return 'Fully Booked';
      return 'Register Now';
    },
    
    calculateDiscount(original, current) {
      return Math.round(((original - current) / original) * 100);
    },
    
    openRegistrationModal(workshop) {
      if (!this.isAvailable(workshop)) return;
      this.selectedWorkshop = workshop;
      this.showModal = true;
      document.body.style.overflow = 'hidden';
    },
    
    closeModal() {
      this.showModal = false;
      this.selectedWorkshop = null;
      this.formData = {
        name: '',
        email: '',
        phone: '',
        participants: '1',
        requirements: ''
      };
      document.body.style.overflow = 'auto';
    },
    
    calculateTotal() {
      if (!this.selectedWorkshop) return 0;
      return this.selectedWorkshop.price * parseInt(this.formData.participants);
    },
    
    async submitRegistration() {
      const $toast = useToast();
      this.isSubmitting = true;
      
      try {
        // Uncomment when backend is ready:
        // await WorkshopService.registerWorkshop({
        //   workshopId: this.selectedWorkshop._id,
        //   ...this.formData
        // });
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        $toast.success('Registration successful! Check your email for confirmation.');
        this.closeModal();
      } catch (error) {
        $toast.error('Registration failed. Please try again.');
      } finally {
        this.isSubmitting = false;
      }
    }
  },
  mounted() {
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-in-out',
        once: true,
        offset: 100
      });
    }
    
    this.getWorkshops();
  },
  beforeUnmount() {
    document.body.style.overflow = 'auto';
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Playfair+Display:wght@400;500;600;700&display=swap');

.workshops-page {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ========== HERO SECTION ========== */
.page-hero {
  height: 300px;
  background: linear-gradient(135deg, rgba(118, 172, 175, 0.9), rgba(98, 152, 155, 0.9));
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-top: 80px;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
}

.hero-content {
  text-align: center;
  color: white;
  position: relative;
  z-index: 2;
}

.page-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  margin-bottom: 20px;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.page-subtitle {
  font-size: clamp(1rem, 2vw, 1.2rem);
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
}

/* ========== FILTER SECTION ========== */
.filter-section {
  padding: 40px 0;
  background: white;
}

.filter-wrapper {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: center;
  align-items: end;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  font-weight: 600;
  color: #2d3748;
  font-size: 0.9rem;
}

.filter-select {
  padding: 10px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  min-width: 200px;
  transition: all 0.3s;
  font-family: 'Inter', sans-serif;
}

.filter-select:focus {
  outline: none;
  border-color: rgb(118, 172, 175);
  box-shadow: 0 0 0 3px rgba(118, 172, 175, 0.1);
}

/* ========== WORKSHOPS GRID ========== */
.workshops-grid {
  padding: 60px 0 100px 0;
  background: #f8fafb;
}

.no-workshops {
  text-align: center;
  padding: 80px 20px;
  color: #718096;
}

.no-workshops i {
  font-size: 4rem;
  color: rgb(118, 172, 175);
  margin-bottom: 20px;
}

.no-workshops h3 {
  font-size: 1.5rem;
  color: #2d3748;
  margin-bottom: 10px;
}

/* Workshop Card */
.workshop-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  transition: all 0.4s;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.workshop-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 40px rgba(0,0,0,0.15);
}

/* Image Section */
.workshop-image-section {
  position: relative;
}

.main-image-wrapper {
  position: relative;
  overflow: hidden;
  height: 300px;
}

.workshop-main-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s;
}

.workshop-card:hover .workshop-main-image {
  transform: scale(1.1);
}

.availability-badge {
  position: absolute;
  top: 15px;
  right: 15px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 700;
  backdrop-filter: blur(10px);
}

.availability-badge.available {
  background: rgba(72, 187, 120, 0.9);
  color: white;
}

.availability-badge.filling-fast {
  background: rgba(237, 137, 54, 0.9);
  color: white;
  animation: pulse 2s infinite;
}

.availability-badge.full {
  background: rgba(245, 101, 101, 0.9);
  color: white;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.category-tag {
  position: absolute;
  top: 15px;
  left: 15px;
  background: rgba(255, 255, 255, 0.95);
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  color: rgb(118, 172, 175);
  display: flex;
  align-items: center;
  gap: 6px;
  text-transform: capitalize;
}

/* Thumbnail Gallery */
.thumbnail-gallery {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 5px;
  padding: 5px;
  background: #f8f9fa;
}

.thumbnail-item {
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: 8px;
  cursor: pointer;
}

.thumbnail-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.thumbnail-item:hover img {
  transform: scale(1.1);
}

.more-images {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.7);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.2rem;
}

/* Workshop Details */
.workshop-details {
  padding: 25px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.workshop-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 12px;
}

.workshop-description {
  color: #718096;
  line-height: 1.7;
  margin-bottom: 20px;
  font-size: 0.95rem;
}

.workshop-info {
  margin-bottom: 20px;
}

.info-row {
  display: flex;
  gap: 20px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4a5568;
  font-size: 0.9rem;
  flex: 1;
  min-width: 180px;
}

.info-item i {
  color: rgb(118, 172, 175);
  width: 16px;
}

/* Learning Section */
.learning-section {
  margin-bottom: 20px;
  padding: 15px;
  background: #f8fafb;
  border-radius: 12px;
}

.section-subtitle {
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 10px;
  font-size: 0.95rem;
}

.learning-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.learning-list li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #4a5568;
  font-size: 0.9rem;
  margin-bottom: 8px;
}

.learning-list i {
  color: rgb(118, 172, 175);
  margin-top: 3px;
  flex-shrink: 0;
}

.materials-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(118, 172, 175, 0.1);
  color: rgb(118, 172, 175);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 20px;
}

/* Workshop Footer */
.workshop-footer {
  margin-top: auto;
  padding-top: 20px;
  border-top: 2px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
}

.price-section {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.price-original {
  color: #a0aec0;
  text-decoration: line-through;
  font-size: 1rem;
}

.price-current {
  font-size: 1.8rem;
  font-weight: 700;
  color: #2d3748;
}

.discount-badge {
  background: #f93a5a;
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
}

.btn-register {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 28px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s;
  text-decoration: none;
}

.btn-register:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(118, 172, 175, 0.4);
  color: white;
}

.btn-register:disabled {
  background: #cbd5e0;
  cursor: not-allowed;
  transform: none;
}

/* ========== WHY JOIN SECTION ========== */
.why-join-section {
  padding: 100px 0;
  background: white;
}

.section-header {
  text-align: center;
  margin-bottom: 10px;
}

.section-subtitle {
  color: rgb(118, 172, 175);
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  display: block;
  margin-bottom: 10px;
}

.section-title-modern {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2rem, 4vw, 2rem);
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 5px;
}

.title-underline {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, rgb(118, 172, 175), rgb(98, 152, 155));
  margin: 0 auto;
  border-radius: 2px;
}

.benefit-card {
  text-align: center;
  padding: 30px 20px;
  transition: transform 0.3s;
}

.benefit-card:hover {
  transform: translateY(-5px);
}

.benefit-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, rgba(118, 172, 175, 0.1), rgba(98, 152, 155, 0.1));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.benefit-icon i {
  font-size: 2rem;
  color: rgb(118, 172, 175);
}

.benefit-card h4 {
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 10px;
}

.benefit-card p {
  color: #718096;
  font-size: 0.95rem;
  line-height: 1.6;
}

/* ========== MODAL ========== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  backdrop-filter: blur(5px);
}

.modal-content {
  background: white;
  border-radius: 20px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
}

.modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0,0,0,0.1);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 1;
}

.modal-close:hover {
  background: rgba(0,0,0,0.2);
  transform: rotate(90deg);
}

.modal-header {
  padding: 40px 40px 20px 40px;
  border-bottom: 2px solid #e2e8f0;
}

.modal-header h3 {
  font-family: 'Playfair Display', serif;
  font-size: 1.8rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 5px;
}

.modal-header p {
  color: rgb(118, 172, 175);
  font-weight: 600;
}

.registration-form {
  padding: 30px 40px 40px 40px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
  font-size: 0.95rem;
}

.form-control {
  width: 100%;
  padding: 12px 18px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s;
  font-family: 'Inter', sans-serif;
}

.form-control:focus {
  outline: none;
  border-color: rgb(118, 172, 175);
  box-shadow: 0 0 0 3px rgba(118, 172, 175, 0.1);
}

textarea.form-control {
  resize: vertical;
  min-height: 100px;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
  border-top: 2px solid #e2e8f0;
  margin-top: 20px;
}

.total-price {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.total-price span {
  color: #718096;
  font-size: 0.9rem;
}

.total-price strong {
  font-size: 1.8rem;
  color: #2d3748;
}

.btn-submit {
  padding: 14px 32px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(118, 172, 175, 0.4);
}

.btn-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

/* ========== MOBILE RESPONSIVE ========== */
@media (max-width: 768px) {
  .page-hero {
    height: 300px;
    margin-top: 70px;
  }
  
  .filter-wrapper {
    flex-direction: column;
  }
  
  .filter-select {
    min-width: 100%;
  }
  
  .workshops-grid {
    padding: 40px 0 60px 0;
  }
  
  .workshop-details {
    padding: 20px;
  }
  
  .workshop-footer {
    flex-direction: column;
    align-items: stretch;
  }
  
  .btn-register {
    width: 100%;
    justify-content: center;
  }
  
  .why-join-section {
    padding: 60px 0;
  }
  
  .modal-content {
    margin: 0;
    border-radius: 20px 20px 0 0;
    max-height: 95vh;
  }
  
  .modal-header,
  .registration-form {
    padding: 30px 20px;
  }
  
  .modal-footer {
    flex-direction: column;
    gap: 20px;
    align-items: stretch;
  }
  
  .btn-submit {
    width: 100%;
    justify-content: center;
  }
}
</style>